import { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import { Form, Formik } from "formik";
import { Search } from "@mui/icons-material";
import { InputAdornment, OutlinedInput, Typography } from "@mui/material";

import PatientListTable from "./PatientListTable";

import patientService from "../../../services/patient";

import { IPaginationGridData } from "../../../types/common/pagination";
import { logger } from "../../../utils/logger";
import { patientTableInitData } from "./utils";
import { titleButton } from "./styles";
import styles from "./styles.module.scss";
import useDebounce from "../../../hooks/useDebounce";
import {
  AppDispatch,
  useAppDispatch,
  useAppSelector,
} from "../../../redux/store";
import { setLoadingAction } from "../../../redux/utils/actions";
import { PAGE_SIZE } from "../../../utils/pageination";
import { userStatus } from "../../../constants/user";
import Chip from "../../common/Chip/Chips";
import Button from "../../common/Button";

interface IFilterType {
  label: string;
  value: string;
  name: string;
}

const debouncedSearch = async (
  dispatch: AppDispatch,
  pageSize: number,
  debouncedValue: string,
  setGridData: React.Dispatch<React.SetStateAction<IPaginationGridData>>,
  setPageNo: React.Dispatch<React.SetStateAction<number>>
) => {
  try {
    dispatch(setLoadingAction(true));
    const searchedResults = await patientService.search(0, pageSize, {
      keyword: debouncedValue,
    });
    const modifiedData = searchedResults?.data?.data?.map((item: any) => ({
      ...item,
      isChecked: false,
    }));
    dispatch(setLoadingAction(false));
    setGridData({
      data: modifiedData,
      result: searchedResults?.data?.result,
    });
    setPageNo(0);
  } catch {
    dispatch(setLoadingAction(false));
  }
};
const getFilterAndDistrictInfo = async (
  dispatch: AppDispatch,
  setFilterAllStatus: React.Dispatch<React.SetStateAction<IFilterType[]>>
) => {
  try {
    dispatch(setLoadingAction(true));
    const info = await patientService.filter();
    dispatch(setLoadingAction(false));
    const statusWithCount: IFilterType[] = [];
    const validFields = [
      userStatus.SUBMITTED,
      userStatus.APPROVED,
      // userStatus.FOLLOW_UP_ONE,
      // userStatus.FOLLOW_UP_THREE,
      // userStatus.FOLLOW_UP_SEVEN,
      // userStatus.FOLLOW_UP_FIFTEEN,
      userStatus.QUERY_RAISED,
      userStatus.ADMITTED,
      // userStatus.DISCHARGED,
      // userStatus.LAMA,
      // userStatus.EXPIRED,
      // userStatus.CLOSED,
      userStatus.ESTIMATE_APPROVAL_REQUEST,
      userStatus.ESTIMATE_APPROVED,
      userStatus.ESTIMATE_QUERY_RAISED,
      userStatus.ENHANCE_APPROVAL_REQUEST,
      userStatus.ENHANCE_APPROVED,
      userStatus.QUERY_RAISED_ON_ENHANCE,
    ];
    info.data.data.statusWithCount?.forEach((item: any) => {
      if (validFields.includes(item.status)) {
        const obj = {
          label: `${item.status} (${item.noOfPatients})`,
          value: item.status,
          name: item.status,
        };
        statusWithCount.push(obj);
      }
    });
    setFilterAllStatus(statusWithCount);
  } catch (e) {
    dispatch(setLoadingAction(false));
  }
};

export default function PatientList() {
  const [gridData, setGridData] = useState<IPaginationGridData>({
    data: [],
    result: 0,
  });

  const dispatch = useAppDispatch();

  const [debouncedValue, searchKeyword, setSearchKeyword] = useDebounce<string>(
    "",
    1000
  );
  const [pageNo, setPageNo] = useState(0);
  const [pageSize] = useState(PAGE_SIZE);
  const [filterAllStatus, setFilterAllStatus] = useState<IFilterType[]>([]);

  const inputHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchKeyword(e?.target?.value);
  };

  useEffect(() => {
    debouncedSearch(dispatch, pageSize, debouncedValue, setGridData, setPageNo);
  }, [debouncedValue, dispatch, pageSize]);

  useEffect(() => {
    getFilterAndDistrictInfo(dispatch, setFilterAllStatus);
  }, [dispatch, gridData]);

  return (
    <>
      <Formik
        enableReinitialize={true}
        initialValues={patientTableInitData}
        // validateOnChange={false}
        onSubmit={(values, { setSubmitting, validateForm, setErrors }) => {
          logger.log(values);
        }}
      >
        {({ values, setFieldValue, resetForm, handleChange, handleSubmit }) => (
          <Form>
            <Box className="d-flex align-items-center justify-content-between mg-b-10">
              <Box className="d-flex">
                <Typography
                  variant="h1"
                  component="h1"
                  sx={titleButton}
                  gutterBottom
                  className="mg-b-0 mg-r-10"
                >
                  Patients
                </Typography>
              </Box>
              <OutlinedInput
                id="outlined-adornment-weight"
                name="searchField"
                value={searchKeyword}
                onChange={inputHandler}
                startAdornment={
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                }
                aria-describedby="outlined-weight-helper-text"
                inputProps={{
                  "aria-label": "weight",
                }}
                className={styles.inputField}
              />
            </Box>
            <Box className={styles.customButtonStyle}>
              {/* {filterAllStatus?.map(({ label }) => (
                <Chip
                  key={label}
                  style={{
                    fontWeight: "600",
                    fontSize: "10px",
                    padding: "3px",
                  }}
                  label={label.split(" ")[0].trim()}
                  extension={label.split(" ")[1].trim()}
                />
              ))} */}
              {filterAllStatus?.map(({ label }) => (
                <Button
                  key={label}
                  color="alert"
                  type="button"
                  style={{
                    height: "20px",
                    padding: "5px 10px",
                    fontSize: "12px",
                    cursor: "default",
                  }}
                >
                  {label.split(" ")[0].trim().replaceAll("_", " ")}{" "}
                  {label.split(" ")[1].trim()}
                </Button>
              ))}
            </Box>
            <Box sx={{ margin: "20px 0" }}>
              <PatientListTable
                searchKeyword={searchKeyword}
                gridData={gridData}
                setGridData={setGridData}
                pageSize={pageSize}
                pageNo={pageNo}
                setPageNo={setPageNo}
              />
            </Box>
          </Form>
        )}
      </Formik>
    </>
  );
}
